import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CircleIcon from "@mui/icons-material/Circle";
import { usePlaygroundContext } from "../Playground";
interface infoScreenprops {
  handleCloseButton: any;
}
// Define the component as React.FC
const IntegrationInfoScreen: React.FC<infoScreenprops> = ({
  handleCloseButton,
}) => {
  const playgroundContext = usePlaygroundContext();
  if (!playgroundContext) {
    throw new Error("PlaygroundContext is not available");
  }
  const { playground, setPlayground } = playgroundContext;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleMainOpen = () => {
    setPlayground((prev) => ({
      ...prev,
      page: 9,
    }));
    scrollToTop();
  };

  const scrollToTop = () => {
    window.scroll(0, 0);
  };

  // Trigger scrollToTop when component is loaded
  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <Box
      sx={{
        p: 3,
        minHeight: "100vh",
        color: "white",
      }}
    >
      <Box sx={{ maxWidth: "1200px", mx: "auto", mb: 5 }}>
        {/* Main heading */}
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: "bold",
            mb: 2,
            color: "#4e7cff",
            background: "linear-gradient(90deg, #538DFF 0%, #A365F3 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
          }}
        >
          Seamless Integration with Your Existing Systems
        </Typography>

        {/* Subheading */}
        <Typography variant="body1" sx={{ mb: 4 }}>
          After the screening is complete, Eximius AI is integration ready to
          push candidate data into your existing systems, including ATS, HRIS,
          VMS, or job boards through custom integrations.
        </Typography>

        {/* Two-column layout */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
          }}
        >
          {/* Left column - Compatible with Popular Platforms */}
          <Box
            sx={{
              width: "50%", // Fixed width
              minWidth: "300px", // Ensures it doesn't shrink too much
              height: "fit-Content", // Adjusts height based on content
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                mb: 3,
                color: "white",
              }}
            >
              Integration ready with Popular :
            </Typography>

            {/* Platforms - Buttons Styled as Chips */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              {[
                "Job Boards",
                "Applicant Tracking System",
                "Human Resource Information System",
                "Vendor Management System",
              ].map((label) => (
                <Button
                  key={label}
                  variant="outlined"
                  sx={{
                    color: "white",
                    borderColor: "rgba(255, 255, 255, 0.3)",
                    borderRadius: "20px",
                    fontSize: "14px",
                    padding: "5px 15px",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "rgba(255, 255, 255, 0.5)",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  {label}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Right column - Enhance Your Recruitment Infrastructure */}
          <Box sx={{ width: { xs: "100%", md: "50%" } }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                mb: 3,
                color: "white",
              }}
            >
              Enhance Your Recruitment Infrastructure
            </Typography>

            {/* Benefits list */}
            <Box sx={{ pl: 0 }}>
              {/* Automated Data Flow */}
              <Box sx={{ display: "flex", mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    mt: 1,
                    mr: 2,
                  }}
                >
                  <CircleIcon sx={{ fontSize: 12, color: "white" }} />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", color: "white", mb: 1 }}
                  >
                    Automated Data Flow
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                    Candidate details, screening results, and documentation sync
                    automatically with your systems, eliminating manual data
                    entry.
                  </Typography>
                </Box>
              </Box>

              {/* Comprehensive Documentation */}
              <Box sx={{ display: "flex", mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    mt: 1,
                    mr: 2,
                  }}
                >
                  <CircleIcon sx={{ fontSize: 12, color: "white" }} />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", color: "white", mb: 1 }}
                  >
                    Comprehensive Documentation
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                    Resumes, scorecards, and screening results are automatically
                    pushed to your preferred systems, creating complete
                    candidate profiles.
                  </Typography>
                </Box>
              </Box>

              {/* Streamlined Scheduling */}
              <Box sx={{ display: "flex", mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    mt: 1,
                    mr: 2,
                  }}
                >
                  <CircleIcon sx={{ fontSize: 12, color: "white" }} />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", color: "white", mb: 1 }}
                  >
                    Streamlined Scheduling
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                    Calendar integration ensures automatic interview scheduling
                    based on both recruiter and candidate availability.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          mt={2}
          display="flex"
          justifyContent="flex-start"
          gap={2}
          flexDirection={isMobile ? "column-reverse" : "row"} // Reversed order on mobile
        >
          <Button
            variant="outlined"
            type="button"
            sx={{
              textTransform: "none",
              backgroundColor: "#7a7a7a",
              borderColor: "rgb(203, 213, 225)",
              color: "#ffffff",
              height: "44px",
              borderRadius: "45px",
              padding: "12px 20px",
              fontSize: "14px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={handleCloseButton}
          >
            Previous
          </Button>

          <Button
            variant="contained"
            sx={{
              backgroundColor: "#0066F4",
              color: "white",
              height: "44px",
              borderRadius: "45px",
              padding: "12px 20px",
              fontSize: "14px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#0052CC",
              },
            }}
            onClick={handleMainOpen}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default IntegrationInfoScreen;
